import { Application, Container, Graphics, SCALE_MODES, Sprite, SVGResource, Texture, utils } from 'pixi.js';
import { debounce } from 'lodash-es';
import JigsawSvgs from './JigsawSvgs.js';
import SvgSpriteMaker
    from './SvgSpriteMaker.js';
import { BevelFilter, AdjustmentFilter, DropShadowFilter, OutlineFilter } from 'pixi-filters';
import { dispatchWindowEvent, randomIntFromInterval, triggerEvent } from '../_utils.js';

export default class Jigsaw
{
    constructor( jigsawContainer )
    {
        this.outerContainer = jigsawContainer;
        this.canvas         = this.outerContainer.querySelector( '.jigsaw-canvas' );

        if ( !this.canvas ) {
            console.error( '[Jigsaw] No canvas to draw on.' )
            return;
        }

        this.resolution            = window.devicePixelRatio;
        this.antialias             = true;
        this.magneticPlaceDistance = 50;
        this.numPiecesLeft         = 0;


        this.prepareGameConfig();

        const startup = async () =>
        {
            await this.buildCanvas();
            await this.setupPlayAreaAndSizes();
            await this.prepareAssets();
            await this.prepareBgContainer();

            this.allPieceContainers = await this.preparePieceContainers();

            this.shufflePieces( this.allPieceContainers );


           triggerEvent(window, 'jigsaw:begin')


        }

        startup();
    }

    get canvasWidth()
    {
        return this.canvas.offsetWidth
    }

    get canvasHeight()
    {
        return this.canvas.offsetHeight;
    }


    getDistance( x1, y1, x2, y2 )
    {
        return Math.hypot( x1 - x2, y1 - y2 );
    }


    prepareGameConfig()
    {

        const jigsawSvgs        = new JigsawSvgs();
        this.jigsawSvg          = jigsawSvgs.getShape();
        this.jigsawVectorShapes = new SvgSpriteMaker( this.jigsawSvg.shape, false );
        // console.log( this.jigsawSvg, '   this.jigsawVectorShapes', this.jigsawVectorShapes )

        const mainImage         = this.canvas.dataset?.mainImage ?? null;
        const fakeImage1        = this.canvas.dataset?.fakeImage1 ?? null;
        const fakeImage2        = this.canvas.dataset?.fakeImage2 ?? null;
        const howManyFakePieces = this.canvas.dataset?.howManyFakePieces ?? null;

        this.gameConfig = {
            mainImage                : mainImage
                                       ? new URL( mainImage, import.meta.url ).href
                                       : new URL( `/resources/images/jigsaw/florian-van-duyn-Dm-qxdynoEc-unsplash-square.jpg`, import.meta.url ).href,
            fakeImage1               : fakeImage1
                                       ? new URL( fakeImage1, import.meta.url ).href
                                       : new URL( `/resources/images/jigsaw/nichika-sakurai-44T1jhG2sfI-unsplash.jpg`, import.meta.url ).href,
            fakeImage2               : fakeImage2
                                       ? new URL( fakeImage2, import.meta.url ).href
                                       : new URL( `/resources/images/jigsaw/tianlei-wu-9QXxKW9UGm0-unsplash.jpg`, import.meta.url ).href,
            howManyFakePieces        : howManyFakePieces ? howManyFakePieces : 2,
            bgContainerRatio         : this.jigsawSvg.ratio,
            bgContainerRelativeHeight: 0.8,
            drawGuideLines           : false,
            bgBorderWidth            : 5,
            referenceImageOpacity    : 0.8,
        }

        this.fctOnDragMove  = this.onDragMove.bind( this );
        this.fctOnDragStart = this.onDragStart.bind( this );
        this.fctOnDragEnd   = this.onDragEnd.bind( this );


        this.filters = {

            // https://filters.pixijs.download/dev/docs/OutlineFilter.html
            outlineFilter: new OutlineFilter( 2, 0x99ff99 ),

            // https://filters.pixijs.download/dev/docs/DropShadowFilter.html
            smallDropShadow: new DropShadowFilter( {
                                                       color   : 0x000020,
                                                       alpha   : 0.5,
                                                       blur    : 2,
                                                       distance: 3,
                                                   } ),

            liftedDropShadow: new DropShadowFilter( {
                                                        color   : 0x000020,
                                                        alpha   : 0.7,
                                                        blur    : 6,
                                                        distance: 20,
                                                    } ),


            // https://filters.pixijs.download/dev/docs/BevelFilter.html
            bevel: new BevelFilter( {
                                        rotation   : 45,
                                        thickness  : 3,
                                        lightColor : 0xffffff,
                                        lightAlpha : 0.7,
                                        shadowColor: 0x000000,
                                        shadowAlpha: 0.7,
                                    } ),

            desaturate: new AdjustmentFilter( {
                                                  saturation: 0.3,
                                              } ),

        }
    }

    async buildCanvas()
    {
        this.app = new Application(
            {

                view           : this.canvas,
                resolution     : this.resolution,
                width          : this.canvasWidth,
                height         : this.canvasHeight,
                antialias      : this.antialias,
                resizeTo       : this.canvas,
                backgroundAlpha: 0,

            } );

        globalThis.__PIXI_APP__ = this.app;

        this.appCenter = {
            x: this.app.screen.width / 2,
            y: this.app.screen.height / 2,
        }

        // this.app.stage.eve = 'static';
        this.app.stage.interactive = true
        this.app.stage.width       = this.canvasWidth;
        this.app.stage.height      = this.canvasHeight;


        this.app.stage.on( 'pointerup', this.fctOnDragEnd );
        this.app.stage.on( 'pointeroutside', () =>
        {
            // console.log( 'pointer left' )
        } );


        const debouncedResize = debounce( () =>
                                          {
                                              this.app.stage.width  = this.canvasWidth;
                                              this.app.stage.height = this.canvasHeight;
                                          }, 500 )

        window.addEventListener( 'resize', debouncedResize )
    }

    setupPlayAreaAndSizes()
    {
        this.playArea      = new Container();
        this.playArea.name = 'playArea';

        let wantedHeight = this.app.screen.height * this.gameConfig.bgContainerRelativeHeight;
        let wantedWidth  = wantedHeight * this.gameConfig.bgContainerRatio;
        if ( wantedWidth > (this.canvasWidth * 0.6) ) {
            wantedWidth  = this.canvasWidth * 0.6;
            wantedHeight = wantedWidth / this.gameConfig.bgContainerRatio;
        }

        const adjustedSize = this.calculateAspectRatioFit( this.jigsawSvg.width, this.jigsawSvg.height, wantedWidth, wantedHeight )
        // console.log( adjustedSize )
        this.gameConfig.piecesSizeFactor = adjustedSize.ratio;
        this.gameConfig.playArea         = {
            x     : (this.canvasWidth - wantedWidth) / 2,
            y     : (this.canvasHeight - wantedHeight) / 2,
            width : wantedWidth,
            height: wantedHeight,

        };


        this.playArea.x      = this.gameConfig.playArea.x;
        this.playArea.y      = this.gameConfig.playArea.y;
        this.playArea.width  = this.gameConfig.playArea.width;
        this.playArea.height = this.gameConfig.playArea.height;


        this.app.stage.addChild( this.playArea );
    }

    async prepareBgContainer()
    {


        this.bgContainer      = new Container();
        this.bgContainer.name = 'bgContainer';

        this.bgSprite = new Sprite( this.puzzleImage );

        this.bgSprite.x      = 0;
        this.bgSprite.y      = 0;
        this.bgSprite.width  = this.gameConfig.playArea.width;
        this.bgSprite.height = this.gameConfig.playArea.height;


        // bgSprite.mask = mask;
        this.bgContainer.width  = this.gameConfig.playArea.width;
        this.bgContainer.height = this.gameConfig.playArea.height;

        this.bgContainer.x = this.gameConfig.playArea.x;
        this.bgContainer.y = this.gameConfig.playArea.y;


        this.bgContainer.addChild( this.bgSprite );
        this.bgSprite.alpha  = this.gameConfig.referenceImageOpacity;
        this.bgSprite.zIndex = 20;

        this.bgPlayingSurface = new Graphics();
        this.bgPlayingSurface.beginFill( 0x04131F );
        this.bgPlayingSurface.drawRect( 0, 0, this.bgContainer.width, this.bgContainer.height );
        this.bgContainer.addChild( this.bgPlayingSurface );
        this.bgPlayingSurface.zIndex = 1;

        const bgMask        = await this.svgToBitmap( this.jigsawSvg.shape, this.bgContainer.width, this.bgContainer.height );
        const bgMaskTexture = await new Texture.from( bgMask );
        const bgMaskSprite  = new Sprite( bgMaskTexture );
        this.bgContainer.addChild( bgMaskSprite );
        this.bgContainer.mask = bgMaskSprite;

        if ( this.gameConfig.drawGuideLines ) {
            const border  = new Graphics();
            border.width  = this.gameConfig.playArea.width;
            border.height = this.gameConfig.playArea.height;
            border.lineStyle( this.gameConfig.bgBorderWidth, 0xFF0000 );
            border.drawRect( 0, 0, this.gameConfig.playArea.width, this.gameConfig.playArea.height );

            this.bgContainer.addChild( border );
        }

        this.app.stage.addChild( this.bgContainer );
    }

    async preparePieceContainers()
    {

        this.piecesContainer      = new Container();
        this.piecesContainer.name = 'pieces-container';

        await this.createPieces( this.piecesContainer, this.puzzleImage, true );


        // this.fakePiecesContainer1      = new Container();
        // this.fakePiecesContainer1.name = 'fake-pieces-container-1';
        // await this.createPieces( this.fakePiecesContainer1, this.fakeImage1, false );
        //
        //
        // this.fakePiecesContainer2      = new Container();
        // this.fakePiecesContainer2.name = 'fake-pieces-container-2';
        // await this.createPieces( this.fakePiecesContainer2, this.fakeImage2, false );

        return [ this.piecesContainer /* this.fakePiecesContainer1, this.fakePiecesContainer2 */ ]
    }

    async createPieces( piecesContainer, image, areRealPieces )
    {
        piecesContainer.sortableChildren = true;
        piecesContainer.x                = this.gameConfig.playArea.x;
        piecesContainer.y                = this.gameConfig.playArea.y;
        piecesContainer.width            = this.gameConfig.playArea.width + (this.gameConfig.offsetX * 2);
        piecesContainer.height           = this.gameConfig.playArea.height + (this.gameConfig.offsetY * 2);

        for ( let i = 0; i < this.jigsawVectorShapes.length; i++ ) {
            const currShape = this.jigsawVectorShapes[ i ];

            const namePrefix = areRealPieces ? 'playable-sprite_' : 'fake-sprite_'

            const pieceSprite  = new Sprite();
            pieceSprite.name   = namePrefix + i;
            pieceSprite.zIndex = 5;

            pieceSprite.x = currShape.layoutCenterX * this.gameConfig.piecesSizeFactor;
            pieceSprite.y = currShape.layoutCenterY * this.gameConfig.piecesSizeFactor;
            pieceSprite.scale.set( this.gameConfig.piecesSizeFactor );

            pieceSprite.originalX = currShape.layoutCenterX //* this.gameConfig.piecesSizeFactor;
            pieceSprite.originalY = currShape.layoutCenterY //* this.gameConfig.piecesSizeFactor;

            const textureSprite = new Sprite( image );
            // pieceSprite.anchor.set(0.5);
            textureSprite.x     = -(currShape.x + (currShape.width / 2));
            textureSprite.y     = -(currShape.y + (currShape.height / 2));
            textureSprite.name  = 'texture';


            pieceSprite.addChild( textureSprite );

            const currMaskBitmap = new Sprite( currShape.maskTextureBitmap );

            currMaskBitmap.name = 'mask-sprite_' + currShape.class;
            currMaskBitmap.anchor.set( 0.5 );
            // currShape.maskSpriteBitmap.scale.set(this.gameConfig.piecesSizeFactor)
            pieceSprite.addChild( currMaskBitmap );

            textureSprite.mask = currMaskBitmap;

            piecesContainer.addChild( pieceSprite );


            pieceSprite.interactive = true;
            pieceSprite.cursor      = 'grab';
            pieceSprite.filters     = [ this.filters.smallDropShadow, this.filters.desaturate ];
            pieceSprite.isRealPiece = areRealPieces;
            pieceSprite.on( 'pointerdown', this.fctOnDragStart )


            pieceSprite.alpha = 1;

            if ( areRealPieces ) {
                this.numPiecesLeft++;
            }


        }

        if ( !areRealPieces ) {
            // console.log( 'fakes', piecesContainer.children );

            piecesContainer.children = piecesContainer.children.map( value => ({ value, sort: Math.random() }) )
                                                      .sort( ( a, b ) => a.sort - b.sort )
                                                      .map( ( { value } ) => value )

            piecesContainer.children = piecesContainer.children.slice( 0, this.gameConfig.howManyFakePieces );
        }
        this.app.stage.addChild( piecesContainer );

        this.draggingPiece = null;
    }


    shufflePieces( allContainers )
    {
        // console.log( 'shufflePieces ( allContainers )', allContainers );

        const xOrigin = this.playArea.x * -1;
        const leftOverFromPlayArea = this.canvas.width - this.gameConfig.playArea.width;
        // console.log(this.gameConfig.playArea, this.playArea, this.app.screen)



        const leftQuadrant = {
            minX : xOrigin,
            maxX :0 ,
            minY :this.gameConfig.playArea.y * -1,
            maxY : (this.gameConfig.playArea.y * -1) + this.canvasHeight,

        };

        const rightQuadrant = {
            minX : this.gameConfig.playArea.width,
            maxX : this.app.screen.width + xOrigin,
            minY :this.playArea.y * -1,
            maxY : (this.playArea.y * -1) + this.canvasHeight,

        };


        allContainers.forEach( container => {
            // console.log(container)
            container.children.forEach( child => {

                const wantedQuadrant = Math.random() < 0.5 ? leftQuadrant : rightQuadrant;


                const childMinX =  wantedQuadrant.minX + (child.getBounds().width / 2)
                const childMaxX =  wantedQuadrant.maxX - (child.getBounds().width / 2);

                const childMinY = wantedQuadrant.minY + (child.getBounds().height / 2)
                const childMaxY = wantedQuadrant.maxY- (child.getBounds().height / 2);

                child.x = randomIntFromInterval( childMinX, childMaxX);
                child.y = randomIntFromInterval( childMinY, childMaxY );

                // console.log('shuffling', child, 'chose ', wantedQuadrant, childMinX, childMaxX, child.x, child.y, child.width, child.getBounds().width , child.height, child.getBounds().height)

            } )
        } )
    }


    onDragStart( event )
    {
        // console.log( 'onDragStart', event )
        this.draggingPiece = event?.target ?? null;

        if ( !this.draggingPiece ) {
            delete this.draggingPiece
            return;
        }
        this.draggingPiece.cursor = 'grabbing';
        this.draggingPiece.zIndex = 100;

        const parentContainer  = this.draggingPiece.parent;
        parentContainer.zIndex = 50;

        this.draggingPieceOriginal = event.data.getLocalPosition( this.draggingPiece.parent );
        // console.log( 'this.draggingPieceOriginal', this.draggingPieceOriginal, this.draggingPiece )
        this.draggingPiece.filters = [ this.filters.liftedDropShadow, this.filters.desaturate ];

        this.app.stage.on( 'pointermove', this.fctOnDragMove );
    }

    onDragMove( event )
    {
        if ( this.draggingPiece ) {
            const eventPoint = event.data.getLocalPosition( this.draggingPiece.parent );
            // console.log('eventPoint', eventPoint)
            // this.draggingPiece.x += eventPoint.x - this.draggingPieceOriginal.x;
            // this.draggingPiece.y += eventPoint.y - this.draggingPieceOriginal.y;
            this.draggingPiece.x = eventPoint.x //
            this.draggingPiece.y = eventPoint.y //

            if ( this.draggingPiece.isRealPiece ) {
                const distanceFromOriginal = this.getDistance(
                    this.draggingPiece.originalX * this.gameConfig.piecesSizeFactor,
                    this.draggingPiece.originalY * this.gameConfig.piecesSizeFactor,
                    this.draggingPiece.x,
                    this.draggingPiece.y,
                )
                // console.log( this.draggingPiece.originalX, this.draggingPiece.originalY, this.draggingPiece.x, this.draggingPiece.y, distanceFromOriginal )

                if ( distanceFromOriginal < this.magneticPlaceDistance ) {
                    if ( distanceFromOriginal > this.magneticPlaceDistance / 2 ) {
                        this.draggingPiece.filters =
                            [
                                this.filters.desaturate, new OutlineFilter( 2, 0xd5e556 ),
                                this.filters.liftedDropShadow,
                            ];
                    }
                    else {
                        this.draggingPiece.filters =
                            [
                                this.filters.desaturate, new OutlineFilter( 2, 0x20d10c ),
                                this.filters.liftedDropShadow,
                            ];
                    }

                }
                else {
                    this.draggingPiece.filters = [ this.filters.liftedDropShadow, this.filters.desaturate ];
                }
            }
        }
    }


    onDragEnd( event )
    {
        // console.log( 'onDragEnd', this.draggingPiece );
        if ( this.draggingPiece ) {
            this.draggingPiece.zIndex = 5;
            let distanceFromOriginal  = 999999;

            const parentContainer  = this.draggingPiece.parent;
            parentContainer.zIndex = 0;

            if ( this.draggingPiece.isRealPiece ) {
                distanceFromOriginal = this.getDistance(
                    this.draggingPiece.originalX * this.gameConfig.piecesSizeFactor,
                    this.draggingPiece.originalY * this.gameConfig.piecesSizeFactor,
                    this.draggingPiece.x,
                    this.draggingPiece.y,
                )

            }

            this.draggingPiece.filters = [ this.filters.smallDropShadow, this.filters.desaturate ];

            this.draggingPiece.cursor = 'grab';
            this.app.stage.off( 'pointermove', this.fctOnDragMove );


            if ( this.draggingPiece.isRealPiece && distanceFromOriginal < this.magneticPlaceDistance ) {
                this.placeAndDeactivatePiece( this.draggingPiece );
            }
            this.draggingPiece = null;
        }
    }

    placeAndDeactivatePiece( piece )
    {
        piece.x           = this.draggingPiece.originalX * this.gameConfig.piecesSizeFactor;
        piece.y           = this.draggingPiece.originalY * this.gameConfig.piecesSizeFactor;
        piece.interactive = false;
        piece.zIndex      = 0;
        piece.alpha       = 1;

        piece.filters = [];
        piece.name += '-placed';

        this.numPiecesLeft--;

        this.checkIfWin();
    }

    checkIfWin()
    {
        // console.log( 'Pieces Left: ' + this.numPiecesLeft );

        if ( this.numPiecesLeft < 1 ) {
            // console.log( 'WIN!!!!' )

            this.bgContainer.alpha = 1;

            triggerEvent( window, 'puzzle-win' );
            triggerEvent(window, 'jigsaw:win')

            this.allPieceContainers.forEach( container => {
                container.alpha = 1

                container.children.forEach( ( piece ) => {
                    piece.off( 'pointerdown', this.fctOnDragStart );
                    piece.interactive = false;
                } );
            } )


        }
    }

    async prepareAssets()
    {

        const textureOptions = {
            crossOrigin    : 'anonymous',
            scaleMode      : SCALE_MODES.LINEAR,
            resourceOptions: {
                autoLoad: true,
            },
        };


        // real image
        const croppedImage = await this.addImage( this.gameConfig.mainImage, '#ffffff' );
        const puzzleImage  = await new Texture.from( croppedImage );


        Texture.addToCache( puzzleImage, 'mainImage' );
        this.puzzleImage = utils.TextureCache[ 'mainImage' ];


        const croppedImage2 = await this.addImage( this.gameConfig.fakeImage1, '#ffffff' );
        const puzzleImage2  = await new Texture.from( croppedImage2 );
        Texture.addToCache( puzzleImage2, 'fakeImage1' );
        this.fakeImage1 = utils.TextureCache[ 'fakeImage1' ];

        const croppedImage3 = await this.addImage( this.gameConfig.fakeImage2, '#ffffff' );
        const puzzleImage3  = await new Texture.from( croppedImage3 );
        Texture.addToCache( puzzleImage3, 'fakeImage2' );
        this.fakeImage2 = utils.TextureCache[ 'fakeImage2' ];

        for ( let i = 0; i < this.jigsawVectorShapes.length; i++ ) {
            const s           = new XMLSerializer().serializeToString( this.jigsawVectorShapes[ i ].singleSvg )
            const encodedData = 'data:image/svg+xml;base64,' + window.btoa( s )


            const maskImage  = await new Texture.from( encodedData );
            const maskSprite = new Sprite( maskImage );

            this.jigsawVectorShapes[ i ].maskSprite = maskSprite;


            const convertedImage        = await this.svgToBitmap( this.jigsawVectorShapes[ i ].singleSvg, this.jigsawVectorShapes[ i ].width, this.jigsawVectorShapes[ i ].height );
            const convertedImageTexture = await new Texture.from( convertedImage );
            // const convertedImageSprite  = new Sprite( convertedImageTexture );

            // this.jigsawVectorShapes[ i ].maskSpriteBitmap = convertedImageSprite;
            this.jigsawVectorShapes[ i ].maskTextureBitmap = convertedImageTexture;
        }
    }


    /**
     * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
     * images to fit into a certain area.
     *
     * @param {Number} srcWidth width of source image
     * @param {Number} srcHeight height of source image
     * @param {Number} maxWidth maximum available width
     * @param {Number} maxHeight maximum available height
     * @param {string} mode cover/contain
     * @return {Object} { width, height }
     */
    calculateAspectRatioFit( srcWidth, srcHeight, maxWidth, maxHeight, mode = 'contain' )
    {

        let ratio
        if ( mode === 'contain' ) {
            ratio = Math.min( maxWidth / srcWidth, maxHeight / srcHeight );
        }
        else {
            ratio = Math.max( maxWidth / srcWidth, maxHeight / srcHeight );
        }

        return { width: srcWidth * ratio, height: srcHeight * ratio, ratio };
    }

    async loadImage( src )
    {
        const image       = new Image();
        const loadPromise = new Promise( resolve =>
                                         {
                                             image.onload = function ()
                                             {
                                                 resolve( this );
                                             }
                                             image.setAttribute( 'crossorigin', 'anonymous' );
                                             image.src = src;
                                         } );

        return await loadPromise;
    }

    async addImage( file, forcedBgColour = null )
    {

        // console.log( 'loadDynamic', file )
        const img                 = await this.loadImage( file );
        const imageOriginalWidth  = img.naturalWidth;
        const imageOriginalHeight = img.naturalHeight;

        const wantedSize = this.calculateAspectRatioFit( imageOriginalWidth, imageOriginalHeight, this.gameConfig.playArea.width, this.gameConfig.playArea.height, 'cover' );

        const imageOriginalWantedWidth  = this.gameConfig.playArea.width / wantedSize.ratio;
        const imageOriginalWantedHeight = this.gameConfig.playArea.height / wantedSize.ratio;
        const imageOriginalX            = (imageOriginalWidth - imageOriginalWantedWidth) / 2;
        const imageOriginalY            = (imageOriginalHeight - imageOriginalWantedHeight) / 2;


        const tempCanvas  = document.createElement( 'canvas' );
        tempCanvas.width  = this.gameConfig.playArea.width / this.gameConfig.piecesSizeFactor;
        tempCanvas.height = this.gameConfig.playArea.height / this.gameConfig.piecesSizeFactor;
        const tempContext = tempCanvas.getContext( '2d' );


        if ( forcedBgColour ) {
            tempContext.fillStyle = forcedBgColour;
            tempContext.rect( 0, 0, tempCanvas.width, tempCanvas.height )
            tempContext.fill();
        }

        tempContext.drawImage(
            img,
            imageOriginalX, // sourceImageX
            imageOriginalY, // sourceImageY
            imageOriginalWantedWidth, // sourceImageCropWidth
            imageOriginalWantedHeight, // SourceImageCropHeight
            0, //(this.canvas.clientWidth - wantedSize.height) / 2, // canvasDrawX
            0, //(this.canvas.clientHeight - wantedSize.height) / 2,// canvasDrawY
            tempCanvas.width, // canvasDrawWidth
            tempCanvas.height, // canvasDrawheight
        );

        // console.log( 'drawImage', {
        //                  img,
        //                  wantedSize,
        //                  imageOriginalX,
        //                  imageOriginalY,
        //                  imageOriginalWantedWidth,
        //                  imageOriginalWantedHeight,
        //                  canvasWidth      : this.canvas.offsetWidth,
        //                  canvasClientWidth: this.canvas.clientWidth,
        //                  canvasHeight     : this.canvas.offsetHeight,
        //                  canvasX          : (this.canvas.offsetWidth - wantedSize.width) / 2,
        //                  canvasY          : (this.canvas.offsetHeight - wantedSize.height) / 2,
        //                  // canvasDrawWidth   : this.wantedWidth,
        //                  // canvasDrawHeight  : this.wantedHeight,
        //              },
        // )


        const loadedImage = await this.loadImage( tempCanvas.toDataURL( 'image/png' ) );

        return loadedImage;
    }


    async svgToBitmap( svg, width, height )
    {
        const s           = new XMLSerializer().serializeToString( svg )
        const encodedData = 'data:image/svg+xml;base64,' + window.btoa( s )

        const img = await this.loadImage( encodedData );

        // document.body.appendChild( img );
        const canvas = document.createElement( 'canvas' )
        const ctx    = canvas.getContext( '2d' );

        canvas.width  = width;
        canvas.height = height;
        ctx.rect( 0, 0, width, height );
        ctx.fill();
        ctx.drawImage( img, 0, 0, width, height )
        return canvas.toDataURL( `image/png}`, 1.0 );
    }
}
